// router.ts — hash-based router.
//
// Routes look like `#/landing`, `#/grant-form?id=42`, `#/public-grants`.
// Hash routing means:
//   • no server-side fallback needed (works with server.py as-is)
//   • copy/paste/share/back/forward all work
//   • the URL path stays at "/" so existing server config is untouched.
//
// A page is just two functions:
//   render(params) → HTML string (gets dropped into <main id="page">)
//   mount?(root, params) → attach listeners, fire data fetches, etc.
//
// Both are optional; a page can render statically or build the DOM in
// mount() and return ''.

import type { BFFUser } from './types';

export interface PageContext {
  params:   URLSearchParams;
  pageData: unknown | null;
  user:     BFFUser | null;
  userRole: 'public' | 'user' | 'approver' | 'admin';
  navigate: (page: string, urlParams?: Record<string, string | number>, pageData?: unknown) => void;
}

export interface Page {
  /** Optional: full-screen routes hide site header + footer (login, signup). */
  fullScreen?: boolean;
  /** Optional: role guard. Returning a string redirects to that route. */
  guard?(ctx: PageContext): string | null;
  /** Render HTML string into the page outlet. */
  render(ctx: PageContext): string;
  /** Called after innerHTML is set. Attach listeners / fetch data here. */
  mount?(root: HTMLElement, ctx: PageContext): void;
  /** Called before the page is torn down. */
  unmount?(): void;
}

const routes: Record<string, Page> = {};

export function registerRoute(name: string, page: Page): void {
  routes[name] = page;
}

export function getRoute(name: string): Page | undefined {
  return routes[name];
}

/** Parse a hash like `#/grant-form?id=42` into { name, params }. */
export function parseHash(hash: string): { name: string; params: URLSearchParams } {
  let h = hash.replace(/^#/, '');
  if (h.startsWith('/')) h = h.slice(1);
  if (!h) return { name: 'landing', params: new URLSearchParams() };
  const qIdx = h.indexOf('?');
  if (qIdx === -1) return { name: h, params: new URLSearchParams() };
  return { name: h.slice(0, qIdx), params: new URLSearchParams(h.slice(qIdx + 1)) };
}

/** Build a hash URL fragment from name + data. */
export function buildHash(name: string, data?: Record<string, string | number>): string {
  if (!data) return `#/${name}`;
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(data)) q.set(k, String(v));
  const qs = q.toString();
  return qs ? `#/${name}?${qs}` : `#/${name}`;
}

/** Programmatic navigation — sets location.hash, which triggers handler.
 *  Optional third arg stashes an arbitrary value (e.g. a freshly-fetched
 *  grant) for the destination page to read via ctx.pageData. The stash
 *  is held until the NEXT navigate() call, so re-renders of the same
 *  page still see it. */
let pageDataStash: unknown = null;

export function navigate(name: string, urlParams?: Record<string, string | number>, pageData?: unknown): void {
  pageDataStash = pageData === undefined ? null : pageData;
  const target = buildHash(name, urlParams);
  if (location.hash === target) {
    // Same hash; force a re-render anyway (e.g. for state refresh).
    window.dispatchEvent(new HashChangeEvent('hashchange'));
  } else {
    location.hash = target;
  }
}

/** Read the stashed pageData. Called by the router when it builds the
 *  PageContext for the current render. Does NOT clear the stash —
 *  navigate() overwrites on the next call. */
export function peekPageData(): unknown { return pageDataStash; }
